import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import * as Mui from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import BlockOutlined from "@mui/icons-material/BlockOutlined";
import CategoryOutlined from "@mui/icons-material/CategoryOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import DnsOutlined from "@mui/icons-material/DnsOutlined";
import ListAltOutlined from "@mui/icons-material/ListAltOutlined";
import DevicesOtherOutlined from "@mui/icons-material/DevicesOtherOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import SwapHorizRounded from "@mui/icons-material/SwapHorizRounded";
import FingerprintOutlined from "@mui/icons-material/FingerprintOutlined";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import SyncOutlined from "@mui/icons-material/SyncOutlined";
import TagOutlined from "@mui/icons-material/TagOutlined";
import ToggleOnOutlined from "@mui/icons-material/ToggleOnOutlined";
import {
  designColors,
  designShell,
  designTheme,
  designType,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
} from "services/constants/designTokens";
import { requestStatusCardsGridSx } from "pages/MyZone/MyZoneApply/RequestStatusCards";
import {
  GlobalBreadcrumb,
  sharedStickyCustomFilterBarSx,
  sharedStickyFooterBarSx,
} from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { IconTile } from "components/DesignSystem/IconSectionCard";
import {
  RequestDetailField,
  detailAccentPalette,
  detailCardSx,
} from "components/DesignSystem/RequestDetailField";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { FilterProvider } from "pages/FilterContext";
import { StatusBadge } from "services/constants";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import {
  balancedColumnWidth,
  monitoringBodyCellSx,
  monitoringHeadCellSx,
  monitoringNameSx,
  monitoringRowSx,
  useMeasuredHeight,
  useMonitoringListAccess,
} from "pages/Monitoring/listUi";
import { showToast } from "services/utils/Status";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { Loader } from "components/Loader";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  DEVICE_PURPOSES,
  deviceToDraft,
  emptyNewDevice,
  formatSync,
  NEW_DEVICE_MODES,
  STATUS_COLORS,
  validateDeviceConfig,
  validateDeviceId,
  validateNewDevice,
} from "./organisationDevicesData";
import { AddDeviceForm } from "./AddDeviceForm";
import { withScreenPermissions } from "components/DesignSystem/ScreenAccess";
import { SCREEN_ACCESS } from "services/utils/screenPermissions";
import {
  API_DEVICE_MODES,
  DEVICE_STATUS_FILTERS,
  mapDeviceLookup,
  mapOrganisationDevice,
  organisationDeviceDeactivateUrl,
  organisationDeviceFetchUrl,
  organisationDevicePayload,
  organisationDeviceReactivateUrl,
  organisationDevicesUrl,
} from "./organisationDevicesApi";

// Standard list headers: drive the table columns, the CustomFilter toolbar
// (search / sort / filter / download) and the "Default View" column picker.
// A row opens its device in the side panel; there is no Action column.
const deviceHeaders = [
  { name: "Device Name", key: "name", type: "string", icon: <DnsOutlined fontSize="small" /> },
  { name: "Device Code", key: "code", type: "string", icon: <TagOutlined fontSize="small" /> },
  { name: "Type", key: "type", type: "autocomplete", icon: <DevicesOtherOutlined fontSize="small" /> },
  { name: "Location", key: "location", type: "autocomplete", icon: <PlaceOutlined fontSize="small" /> },
  { name: "Last Sync", key: "lastSync", type: "date", customFormatter: (device) => { const { date, time } = formatSync(device.lastSync); return (date + " " + time).trim(); }, icon: <SyncOutlined fontSize="small" /> },
  { name: "Status", key: "status", type: "autocomplete", icon: <ToggleOnOutlined fontSize="small" /> },
];

// Fixed-layout widths (sum to 100%). Device Name has its own share instead of
// absorbing the remainder, which left it far too wide. Hidden columns'
// shares are redistributed by balancedColumnWidth.
const columnWidths = {
  name: "24%",
  code: "14%",
  type: "14%",
  location: "18%",
  lastSync: "15%",
  status: "15%",
};

// Status cards: same shared StatCard row as the Approvals / My Requests
// screens. Active = admin status, not Online/Offline connectivity.
const STATUS_CARD_DEFS = {
  All: { label: "All Devices", icon: ListAltOutlined, accent: "#1D4ED8", tint: "#EEF4FF" },
  Active: { label: "Active", icon: CheckCircleOutlined, accent: "#16A34A", tint: "#EAFBF1" },
  Inactive: { label: "Inactive", icon: BlockOutlined, accent: designColors.slate600, tint: designColors.slate100 },
};

const DeviceIcon = ({ type, ...props }) => {
  const Icon = { attendance: FingerprintOutlined, zkteco: FingerprintOutlined, patrolling: PlaceOutlined }[String(type ?? "").trim().toLowerCase()] || DnsOutlined;
  return <Icon {...props} />;
};

const cellTitle = (key, device) => {
  if (key === "lastSync") {
    const { date, time } = formatSync(device.lastSync);
    return (date + " " + time).trim();
  }
  return device[key] || "";
};

function OrganisationDevices({ permissions }) {
  // Edit permission: add and update. This screen has no delete action.
  const canEdit = permissions?.canEdit !== false;
  const history = useHistory();
  // Guessed backend names; until an "Organisation" module with a "Devices"
  // submodule is provisioned, saved views stay switched off.
  const { domain, userDetails, subModuleId, canSaveViews } =
    useMonitoringListAccess("Devices", "Organisation");
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const lookupRequestRef = useRef(0);
  const lookupBusyRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  // null until CustomFilter computes its first filtered set, so the table
  // shows the rows straight away instead of flashing an empty state.
  const [filteredDevices, setFilteredDevices] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  // { mode: "add" | "edit", draft, errors, tab }
  const [panel, setPanel] = useState(null);
  // The device shown in the read-only details panel. The edit icon in its
  // header swaps it for the form panel; it returns when the form closes.
  const [viewId, setViewId] = useState(null);
  // Active / Inactive admin-status filter, picked from the status cards. The
  // full list is loaded once so every card can show its count; this filter is
  // applied on the screen (not the CustomFilter Status column, which is
  // connectivity Online/Offline/Unknown).
  const [statusFilter, setStatusFilter] = useState("All");
  // The device being deactivated: { id, name } plus the reason being typed.
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [deactivateError, setDeactivateError] = useState("");
  const [deactivateSaving, setDeactivateSaving] = useState(false);
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [reactivateSaving, setReactivateSaving] = useState(false);
  // One expand state for both panels, so Edit opened from a full-screen
  // Device Details stays full screen. Collapses once every panel is closed.
  const [panelExpanded, setPanelExpanded] = useState(false);
  const togglePanelExpanded = useCallback(
    () => setPanelExpanded((previous) => !previous),
    [],
  );
  const viewOpen = devices.some((device) => device.id === viewId);
  useEffect(() => {
    if (!viewOpen && !panel) setPanelExpanded(false);
  }, [viewOpen, panel]);
  const [customFilterRef, customFilterHeight] = useMeasuredHeight();
  const [footerRef, footerHeight] = useMeasuredHeight();
  const resetPage = useCallback(() => setPage(0), []);
  useEffect(() => {
    let active = true;
    lookupRequestRef.current += 1;
    lookupBusyRef.current = false;
    setLoading(true);
    setLoadError("");
    setDevices([]);
    setFilteredDevices(null);
    setPanel(null);
    setViewId(null);
    customGetAPI(organisationDevicesUrl(domain, null)).then(result => {
      if (!active) return;
      if (result?.ok && Array.isArray(result.responseDetails?.data)) {
        setDevices(result.responseDetails.data.map(mapOrganisationDevice));
        setPage(0);
      } else setLoadError(result?.message || "Organisation devices could not be loaded.");
      setLoading(false);
    }).catch(error => {
      if (!active) return;
      setLoadError(error.message || "Organisation devices could not be loaded.");
      setLoading(false);
    });
    return () => { active = false; };
  }, [domain, reloadKey]);

  // Refreshes the list in place after Save / Deactivate / Reactivate: keeps
  // viewId and the open side panel (so an admin sees the device they just
  // changed), unlike the effect above (a fresh load / Retry / filter change).
  const refreshDevices = async () => {
    const refreshed = await customGetAPI(organisationDevicesUrl(domain, null));
    if (!mountedRef.current) return false;
    if (refreshed?.ok && Array.isArray(refreshed.responseDetails?.data)) {
      setDevices(refreshed.responseDetails.data.map(mapOrganisationDevice));
      setFilteredDevices(null);
      setLoadError("");
      return true;
    }
    setLoadError("Device saved, but the list could not be refreshed. Please retry.");
    return false;
  };
  const { visibleHeaders: selectedHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "organisation_devices_saved_views",
    fullHeaders: deviceHeaders,
    screenAccess: canSaveViews,
    onViewChange: resetPage,
  });
  const visibleHeaders = useMemo(() => [
    ...selectedHeaders.filter(header => header.key !== "status"),
    ...selectedHeaders.filter(header => header.key === "status"),
  ], [selectedHeaders]);

  const statusCounts = useMemo(() => {
    const activeCount = devices.filter((device) => device.active !== false).length;
    return { All: devices.length, Active: activeCount, Inactive: devices.length - activeCount };
  }, [devices]);
  const statusDevices = useMemo(() => (
    statusFilter === "All"
      ? devices
      : devices.filter((device) => (device.active !== false) === (statusFilter === "Active"))
  ), [devices, statusFilter]);

  // The filtered set is a snapshot from CustomFilter; map it back to the
  // current records so edits show immediately, and re-apply the status card
  // so a device that was just (de)activated leaves the wrong card at once.
  const visibleDevices = useMemo(() => {
    if (filteredDevices === null) return statusDevices;
    const byId = new Map(statusDevices.map((device) => [device.id, device]));
    return filteredDevices
      .map((device) => byId.get(device.id))
      .filter(Boolean);
  }, [statusDevices, filteredDevices]);
  const safePage = Math.min(
    page,
    Math.max(0, Math.ceil(visibleDevices.length / rowsPerPage) - 1),
  );
  const pageStart = safePage * rowsPerPage;

  const openDevice = (device) =>
    canEdit && setPanel({ mode: "edit", draft: deviceToDraft(device), errors: {} });
  const openAdd = () => {
    if (!canEdit) return;
    lookupRequestRef.current += 1;
    lookupBusyRef.current = false;
    setPanel({ mode: "add", draft: emptyNewDevice(), errors: {} });
  };

  // Add Device: editing the ID invalidates whatever was fetched for the old one.
  const setNewDraft = (key, value) => {
    if (savingRef.current || key === "mode") return;
    if (panel?.mode === "add" && (key === "name" || key === "location" || (key !== "deviceId" && panel.draft.lookup?.status !== "found"))) return;
    if (key === "deviceId") {
      lookupRequestRef.current += 1;
      lookupBusyRef.current = false;
    }
    setPanel((previous) => ({
      ...previous,
      draft: {
        ...previous.draft,
        [key]: value,
        ...(key === "deviceId" ? { lookup: null, mode: "", name: "", location: "" } : {}),
      },
      errors: { ...previous.errors, [key]: "" },
    }));
  };

  const persistDevice = async () => {
    if (!canEdit || savingRef.current) return;
    const isUpdate = panel.mode === "edit";
    const lookup = panel.draft.lookup;
    const draft = isUpdate ? panel.draft : {
      ...panel.draft,
      mode: lookup?.status === "found" ? lookup.mode : "",
      name: lookup?.status === "found" ? lookup.name : "",
      location: lookup?.status === "found" ? lookup.location : "",
    };
    const errors = isUpdate ? validateDeviceConfig(draft) : validateNewDevice(draft, devices);
    if (!API_DEVICE_MODES.includes(draft.mode)) errors.mode = "Device mode must be supplied by the server.";
    if (Object.keys(errors).length) {
      setPanel({ ...panel, errors });
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const result = await customAPI(organisationDevicesUrl(domain, isUpdate ? draft.id : null), isUpdate ? "PUT" : "POST", organisationDevicePayload(draft));
      if (!mountedRef.current) return;
      if (!result?.ok) return;
      setPanel(null);
      showToast("success", isUpdate ? "Device updated." : "Device added.");
      if (await refreshDevices() && !isUpdate) setPage(0);
    } catch (error) {
      if (mountedRef.current) showToast("error", error.message || "Device could not be saved.");
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const openDeactivate = (device) => {
    if (!canEdit) return;
    setDeactivateReason("");
    setDeactivateError("");
    setDeactivateTarget(device);
  };
  const closeDeactivate = () => {
    if (deactivateSaving) return;
    setDeactivateTarget(null);
    setDeactivateReason("");
    setDeactivateError("");
  };
  const confirmDeactivate = async () => {
    if (!deactivateTarget || deactivateSaving) return;
    const reason = deactivateReason.trim();
    if (!reason) {
      setDeactivateError("A reason is required.");
      return;
    }
    setDeactivateSaving(true);
    try {
      const result = await customAPI(
        organisationDeviceDeactivateUrl(domain, deactivateTarget.id),
        "POST",
        { reason },
      );
      if (!mountedRef.current) return;
      if (!result?.ok) return;
      setDeactivateTarget(null);
      setDeactivateReason("");
      showToast("success", "Device deactivated.");
      await refreshDevices();
    } catch (error) {
      if (mountedRef.current) showToast("error", error.message || "Device could not be deactivated.");
    } finally {
      if (mountedRef.current) setDeactivateSaving(false);
    }
  };

  const openReactivate = (device) => { if (canEdit) setReactivateTarget(device); };
  const closeReactivate = () => { if (!reactivateSaving) setReactivateTarget(null); };
  const confirmReactivate = async () => {
    if (!reactivateTarget || reactivateSaving) return;
    setReactivateSaving(true);
    try {
      const result = await customAPI(
        organisationDeviceReactivateUrl(domain, reactivateTarget.id),
        "POST",
        {},
      );
      if (!mountedRef.current) return;
      if (!result?.ok) return;
      setReactivateTarget(null);
      showToast("success", "Device reactivated.");
      await refreshDevices();
    } catch (error) {
      if (mountedRef.current) showToast("error", error.message || "Device could not be reactivated.");
    } finally {
      if (mountedRef.current) setReactivateSaving(false);
    }
  };

  const fetchDeviceInformation = async () => {
    if (savingRef.current || lookupBusyRef.current || panel?.mode !== "add") return;
    const deviceId = String(panel.draft.deviceId).trim();
    const error = validateDeviceId(panel.draft.deviceId, devices);
    setPanel(previous => ({
      ...previous,
      draft: { ...previous.draft, mode: "", name: "", location: "", lookup: error ? null : { status: "loading" } },
      errors: { ...previous.errors, deviceId: error, mode: "" },
    }));
    if (error) return;
    lookupBusyRef.current = true;
    const requestId = ++lookupRequestRef.current;
    const applyLookup = lookup => {
      if (!mountedRef.current || requestId !== lookupRequestRef.current) return;
      setPanel(previous => {
        if (previous?.mode !== "add" || String(previous.draft.deviceId).trim() !== deviceId) return previous;
        return {
          ...previous,
          draft: {
            ...previous.draft, lookup,
            ...(lookup.status === "found" ? { mode: lookup.mode, name: lookup.name, location: lookup.location } : { mode: "" }),
          },
          errors: lookup.status === "found" ? { ...previous.errors, deviceId: "", mode: "", name: "" } : previous.errors,
        };
      });
    };
    try {
      const result = await customGetAPI(organisationDeviceFetchUrl(domain, deviceId));
      applyLookup(result?.code === 404
        ? { status: "notFound" }
        : result?.ok ? mapDeviceLookup(result.responseDetails, deviceId) : { status: "unavailable" });
    } catch {
      applyLookup({ status: "unavailable" });
    } finally {
      if (requestId === lookupRequestRef.current) lookupBusyRef.current = false;
    }
  };

  const renderCell = (key, device) => {
    switch (key) {
      case "name":
        return (
          <Mui.Typography
            noWrap
            sx={monitoringNameSx(viewId === device.id)}
          >
            {device.name}
          </Mui.Typography>
        );
      case "type":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <DeviceIcon type={device.type} fontSize="small" sx={{ color: designColors.slate600 }} />
            <Mui.Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {device.type}
            </Mui.Box>
          </Mui.Stack>
        );
      case "status":
        return (
          <StatusBadge color={STATUS_COLORS[device.status]} label={device.status} />
        );
      case "lastSync": {
        const { date, time } = formatSync(device.lastSync);
        return (
          <>
            <Mui.Box>{date}</Mui.Box>
            {time && <Mui.Box sx={{ color: designColors.slate600 }}>{time}</Mui.Box>}
          </>
        );
      }
      default:
        return device[key];
    }
  };

  const draft = panel?.draft;
  const errors = panel?.errors || {};
  const isEdit = panel?.mode === "edit";
  const viewing = devices.find((device) => device.id === viewId);
  const optionTitle = (options, value) =>
    options.find((option) => option.value === value)?.title || value;
  const viewFields = viewing
    ? [
        { icon: TagOutlined, label: "Device ID", value: viewing.code, accent: detailAccentPalette.indigo },
        { icon: TagOutlined, label: "Serial Number", value: viewing.serialNumber || "—", accent: detailAccentPalette.indigo },
        { icon: SwapHorizRounded, label: "Cauver Device Mode", value: optionTitle(NEW_DEVICE_MODES, viewing.mode), accent: detailAccentPalette.green },
        { icon: CategoryOutlined, label: "Device Type", value: optionTitle(DEVICE_PURPOSES, viewing.purpose || "Attendance"), accent: detailAccentPalette.purple },
        { icon: PlaceOutlined, label: "Device Location", value: viewing.location, accent: detailAccentPalette.blue },
        { icon: SyncOutlined, label: "Last Sync", value: cellTitle("lastSync", viewing), accent: detailAccentPalette.pink },
        { icon: ToggleOnOutlined, label: "Status", value: viewing.active === false ? "Inactive" : "Active", accent: detailAccentPalette.green },
        // History, not current state: shown whenever present, even while
        // Active again — "last deactivated" and "last reactivated" are kept
        // side by side rather than cleared on Reactivate. Admin status
        // (Active/Inactive) is independent of Online/Offline connectivity,
        // shown separately in the header chip above.
        ...(viewing.deactivatedAt
          ? [
              { icon: BlockOutlined, label: "Last Deactivated On", value: viewing.deactivatedAt, accent: detailAccentPalette.orange },
              { icon: BlockOutlined, label: "Last Deactivated By", value: viewing.deactivatedBy || "—", accent: detailAccentPalette.orange },
              { icon: BlockOutlined, label: "Deactivation Reason", value: viewing.deactivationReason || "—", accent: detailAccentPalette.orange },
            ]
          : []),
        ...(viewing.reactivatedAt
          ? [
              { icon: CheckCircleOutlined, label: "Last Reactivated On", value: viewing.reactivatedAt, accent: detailAccentPalette.green },
              { icon: CheckCircleOutlined, label: "Last Reactivated By", value: viewing.reactivatedBy || "—", accent: detailAccentPalette.green },
            ]
          : []),
      ]
    : [];

  if (loading) return <Loader info="Loading..." />;
  return (
    <Mui.ThemeProvider theme={designTheme}>
      <FilterProvider domain={domain} userDetails={userDetails} subModuleId={subModuleId}>
        <Mui.Box sx={{ ...designShell.pageSurface, p: { xs: 2, md: 3 } }}>
          <Mui.Stack spacing={2}>
            <GlobalBreadcrumb
              trail={[
                {
                  label: "Administration",
                  onClick: () => history.push("/admin/administration/user"),
                },
                {
                  label: "Organisation",
                  onClick: () =>
                    history.push("/admin/administration/organisation/details"),
                },
                { label: "Devices" },
              ]}
            />
            <Mui.Box>
              <Mui.Typography component="h1" sx={designType.pageTitle}>
                Organisation Devices
              </Mui.Typography>
              <Mui.Typography sx={designType.pageSubtitle}>
                Manage biometric and attendance devices for your organisation.
                Devices are organisation-level and can be used across multiple
                companies.
              </Mui.Typography>
            </Mui.Box>
            {loadError && <Mui.Alert severity="error" action={<Mui.Button color="inherit" onClick={() => setReloadKey(value => value + 1)}>Retry</Mui.Button>}>{loadError}</Mui.Alert>}

            <Mui.Box sx={{ ...requestStatusCardsGridSx, mb: 0 }}>
              {DEVICE_STATUS_FILTERS.map((key) => {
                const def = STATUS_CARD_DEFS[key];
                return (
                  <StatCard
                    activeBorder
                    key={key}
                    icon={def.icon}
                    title={def.label}
                    value={loading ? "—" : statusCounts[key]}
                    accent={def.accent}
                    tint={def.tint}
                    isActive={statusFilter === key}
                    onClick={() => { setStatusFilter(key); resetPage(); }}
                  />
                );
              })}
            </Mui.Box>

            <Mui.Box
              ref={customFilterRef}
              sx={{ ...designShell.sectionCard, ...sharedStickyCustomFilterBarSx }}
            >
              <CustomFilter
                title="devices"
                titleOverride={titleOverride}
                filterHeaders={visibleHeaders}
                data={statusDevices}
                filteredData={visibleDevices}
                filteredDataReady={filteredDevices !== null}
                onFilteredDataChange={setFilteredDevices}
                screenAccess={true}
                hideCount={true}
                labeledToolbar={true}
                downloadFormats={["excel", "pdf", "csv"]}
                searchPlaceholder="Search devices by name, code or location..."
                newButtonComponent={canEdit &&
                  <PrimaryButton
                    startIcon={<AddRounded />}
                    onClick={openAdd}
                    disabled={Boolean(loadError) || saving}
                    sx={{ minWidth: 0, minHeight: 40, whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    Add Device
                  </PrimaryButton>
                }
              />
            </Mui.Box>

            <Mui.Box sx={{ ...designShell.sectionCard, width: "100%" }}>
              <Mui.TableContainer
                sx={{
                  width: "100%",
                  // Visible, not auto: the sticky header needs the page as its
                  // scroll parent, and the fixed-layout table always fits.
                  overflowX: "visible",
                  pb: footerHeight ? footerHeight + "px" : 0,
                }}
              >
                <ApproveTable
                  stickyHeader
                  aria-label="Organisation devices"
                  sx={{ width: "100%", tableLayout: "fixed" }}
                >
                  <Mui.TableHead sx={designShell.tableStickyHead}>
                    <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                      {visibleHeaders.map((header) => (
                        <Mui.TableCell
                          key={header.key}
                          scope="col"
                          title={header.name}
                          sx={{
                            ...monitoringHeadCellSx(customFilterHeight),
                            width: balancedColumnWidth(header.key, visibleHeaders, deviceHeaders, columnWidths),
                          }}
                        >
                          {header.name}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {visibleDevices.length === 0 ? (
                      <TableEmptyState
                        dataLength={devices}
                        colSpan={visibleHeaders.length}
                      />
                    ) : (
                      visibleDevices
                        .slice(pageStart, pageStart + rowsPerPage)
                        .map((device) => (
                          <Mui.TableRow
                            key={device.id}
                            onClick={() => setViewId(device.id)}
                            sx={monitoringRowSx(viewId === device.id)}
                          >
                            {visibleHeaders.map((header) => (
                              <Mui.TableCell
                                key={header.key}
                                title={cellTitle(header.key, device)}
                                sx={{
                                  ...monitoringBodyCellSx,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {renderCell(header.key, device)}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>
                        ))
                    )}
                  </Mui.TableBody>
                </ApproveTable>
              </Mui.TableContainer>
              <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>
                {paginationFooter({
                  page: safePage,
                  rowsPerPage,
                  total: visibleDevices.length,
                  itemLabel: "devices",
                  rowsPerPageOptions,
                  onPageChange: setPage,
                  onRowsPerPageChange: (event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                  },
                })}
              </Mui.Box>
            </Mui.Box>
          </Mui.Stack>
        </Mui.Box>
      </FilterProvider>

      <GlobalSidePanel
        // Steps aside while the form panel is up, and comes back in the same
        // (side or full-screen) layout once that closes.
        open={Boolean(viewing) && !panel}
        expanded={panelExpanded && Boolean(viewing) && !panel}
        onToggleExpand={togglePanelExpanded}
        onClose={() => setViewId(null)}
        title="Device Details"
        subtitle="Device registered for this organisation."
        bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}
        headerActions={
          viewing && canEdit && (
            <>
              {/* App tooltip style, and the same soft icon button the panel
                  uses for its own header icons. */}
              {viewing.active === false ? (
                <Mui.Tooltip title="Reactivate" arrow slotProps={tooltipStyleProps}>
                  <Mui.IconButton
                    size="small"
                    aria-label={"Reactivate " + viewing.name}
                    onClick={() => openReactivate(viewing)}
                    sx={{ ...designShell.panelSoftIconButton, color: "#16A34A" }}
                  >
                    <CheckCircleOutlined fontSize="small" />
                  </Mui.IconButton>
                </Mui.Tooltip>
              ) : (
                <Mui.Tooltip title="Deactivate" arrow slotProps={tooltipStyleProps}>
                  <Mui.IconButton
                    size="small"
                    aria-label={"Deactivate " + viewing.name}
                    onClick={() => openDeactivate(viewing)}
                    sx={{ ...designShell.panelSoftIconButton, color: "#DC2626" }}
                  >
                    <BlockOutlined fontSize="small" />
                  </Mui.IconButton>
                </Mui.Tooltip>
              )}
              <Mui.Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
                <Mui.IconButton
                  size="small"
                  aria-label={"Edit " + viewing.name}
                  onClick={() => openDevice(viewing)}
                  sx={{
                    ...designShell.panelSoftIconButton,
                    color: designColors.slate500,
                  }}
                >
                  <EditOutlined fontSize="small" />
                </Mui.IconButton>
              </Mui.Tooltip>
            </>
          )
        }
      >
        {viewing && (
          <Mui.Stack spacing={2}>
            <Mui.Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid " + designColors.slate200,
              }}
            >
              <IconTile icon={<DeviceIcon type={viewing.type} />} />
              <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
                <Mui.Typography noWrap sx={designType.panelTitle}>
                  {viewing.name}
                </Mui.Typography>
                <Mui.Typography noWrap sx={designType.helperText}>
                  {viewing.type} | {viewing.code}
                </Mui.Typography>
              </Mui.Box>
              <RequestStatusChip
                status={viewing.status}
                colorCode={STATUS_COLORS[viewing.status]}
              />
            </Mui.Stack>
            <Mui.Box sx={detailCardSx}>
              {viewFields.map((field, index) => (
                <RequestDetailField
                  key={field.label}
                  {...field}
                  isLast={index === viewFields.length - 1}
                />
              ))}
            </Mui.Box>
          </Mui.Stack>
        )}
      </GlobalSidePanel>

      <GlobalSidePanel
        open={Boolean(panel)}
        expanded={panelExpanded && Boolean(panel)}
        onToggleExpand={togglePanelExpanded}
        onClose={() => { if (!savingRef.current) setPanel(null); }}
        title={isEdit ? "Edit Device" : "Add Device"}
        subtitle={
          isEdit
            ? "Update how this device is used in the organisation."
            : "Fetch the device information and configure it for the organisation."
        }
        bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}
        footer={
          <Mui.Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={1.5}
            justifyContent="flex-end"
            sx={{ width: "100%" }}
          >
            <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
              <SecondaryButton disabled={saving} onClick={() => setPanel(null)}>Cancel</SecondaryButton>
              <PrimaryButton disabled={saving || (!isEdit && draft?.lookup?.status !== "found")} onClick={persistDevice}>
                {saving ? "Saving..." : isEdit ? "Save Changes" : "Save Device"}
              </PrimaryButton>
            </Mui.Stack>
          </Mui.Stack>
        }
      >
        {panel && (
          <Mui.Stack component="fieldset" disabled={saving} spacing={2.5} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
            <AddDeviceForm
              isEdit={isEdit}
              draft={draft}
              errors={errors}
              onChange={setNewDraft}
              onFetch={isEdit ? undefined : fetchDeviceInformation}
              modeOptions={NEW_DEVICE_MODES.filter(option => API_DEVICE_MODES.includes(option.value))}
            />
          </Mui.Stack>
        )}
      </GlobalSidePanel>

      <ConfirmationDialog
        open={Boolean(deactivateTarget)}
        onClose={closeDeactivate}
        title="Deactivate Device"
        confirmText="Deactivate"
        onConfirm={confirmDeactivate}
        showChildren
        disableAction={deactivateSaving}
        handledisabled={deactivateSaving}
      >
        <Mui.Stack spacing={1.5}>
          <Mui.Typography sx={designType.helperText}>
            Punches from <strong>{deactivateTarget?.name}</strong> made from now on
            will be accepted but not processed into attendance, until it is
            reactivated. This does not affect past punches.
          </Mui.Typography>
          <Mui.TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Reason"
            required
            autoFocus
            value={deactivateReason}
            onChange={(event) => { setDeactivateReason(event.target.value); setDeactivateError(""); }}
            error={Boolean(deactivateError)}
            helperText={deactivateError || "Why is this device being deactivated?"}
            disabled={deactivateSaving}
            inputProps={{ maxLength: 500, "aria-label": "Deactivation reason" }}
          />
        </Mui.Stack>
      </ConfirmationDialog>

      <ConfirmationDialog
        open={Boolean(reactivateTarget)}
        onClose={closeReactivate}
        title="Reactivate Device"
        confirmText="Confirm"
        onConfirm={confirmReactivate}
        disableAction={reactivateSaving}
        handledisabled={reactivateSaving}
        message={
          "Punches from " + (reactivateTarget?.name || "this device") +
          " made from now on will be processed normally. Punches made while" +
          " it was inactive are not reprocessed automatically — use" +
          " Reprocess on those events in Event Trace if needed."
        }
      />

    </Mui.ThemeProvider>
  );
}

export default withScreenPermissions(OrganisationDevices, SCREEN_ACCESS.organisationDevices, "Organisation Devices");
